import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {FilterDefinition, FilterValues} from '../../models/vte';
import {ApiService} from '../api.service';
import {AuthService} from '../auth.service';
import {formatProviderLabel} from './vision-customer-labels';
import {
    PROVIDER_EVENT_PROCESSING_STATUSES,
    resolveProviderEventStatusBadge,
    TRANSACTION_STATUS_GROUP_OPTIONS,
} from './vte.constants';
import {OperationsOrganizationService, OrganizationOption} from './operations-organization.service';
import {VisionConfigService} from './vision-config.service';

export type CameraFilterOption = {
    id: number;
    label: string;
};

export type CameraListOptionSource = {
    id: number;
    camera_esn?: string | null;
    /** Camera Name from C-02/C-03; API field remains ``description``. */
    description?: string | null;
};

/**
 * Camera Analytics / operations camera dropdown label (C-04).
 * Named cameras: ``Camera Name (ESN)``. Empty/whitespace name: ESN alone.
 */
export function formatCameraDropdownLabel(camera: CameraListOptionSource): string {
    const esn = (camera.camera_esn || '').trim();
    const name = (camera.description || '').trim();
    const identity = esn || `Camera ${camera.id}`;
    if (!name) {
        return identity;
    }
    return `${name} (${identity})`;
}

@Injectable({
    providedIn: 'root',
})
export class OperationsFiltersService {
    constructor(
        private visionConfig: VisionConfigService,
        private organizationService: OperationsOrganizationService,
        private apiService: ApiService,
        private auth: AuthService,
    ) {
    }

    loadCameraOptions(orgId?: number | string): Observable<CameraFilterOption[]> {
        let resolvedOrgId: number | string = '';
        if (this.auth.isSuperUser()) {
            if (orgId !== undefined && orgId !== null && String(orgId).trim() !== '') {
                resolvedOrgId = orgId;
            }
        } else {
            resolvedOrgId = orgId ?? this.organizationService.getCurrentOrganizationId() ?? '';
        }
        return this.apiService.viewCameras(1, 200, '', resolvedOrgId).pipe(
            map((response: { results?: CameraListOptionSource[] }) =>
                (response?.results || []).map((camera) => ({
                    id: camera.id,
                    label: formatCameraDropdownLabel(camera),
                })),
            ),
        );
    }

    getTransactionFilterDefinitions(
        cameras: CameraFilterOption[] = [],
        organizations: OrganizationOption[] = [],
    ): FilterDefinition[] {
        const filters: FilterDefinition[] = [
            {
                key: 'search',
                label: 'Search',
                controlType: 'search',
                placeholder: 'Transaction ID or vehicle number',
            },
            {
                key: 'status_group',
                label: 'Status',
                controlType: 'select',
                options: TRANSACTION_STATUS_GROUP_OPTIONS.map((option) => ({
                    value: option.value,
                    label: option.label,
                })),
            },
            {
                key: 'camera_id',
                label: 'Camera',
                controlType: 'select',
                options: cameras.map((camera) => ({
                    value: String(camera.id),
                    label: camera.label,
                })),
            },
        ];

        if (this.organizationService.canFilterByOrganization()) {
            filters.push({
                key: 'org_id',
                label: 'Organization',
                controlType: 'select',
                options: organizations.map((organization) => ({
                    value: String(organization.id),
                    label: organization.name,
                })),
            });
        }

        return filters;
    }

    getProviderEventFilterDefinitions(
        cameras: CameraFilterOption[] = [],
        organizations: OrganizationOption[] = [],
    ): FilterDefinition[] {
        const showImplementationDetails = this.auth.isSuperUser();
        const providerOptions = (this.visionConfig.supportedProviders || [])
            .filter((provider) => provider.is_active !== false)
            .map((provider) => ({
                value: provider.provider_id,
                label: formatProviderLabel(provider.provider_id, showImplementationDetails),
            }));

        const filters: FilterDefinition[] = [
            {
                key: 'search',
                label: 'Search',
                controlType: 'search',
                placeholder: 'Event ID or provider reference',
            },
            {
                key: 'processing_status',
                label: 'Status',
                controlType: 'select',
                options: PROVIDER_EVENT_PROCESSING_STATUSES.map((status) => ({
                    value: status,
                    label: resolveProviderEventStatusBadge(status)?.label || status,
                })),
            },
            {
                key: 'provider_id',
                label: 'Provider',
                controlType: 'select',
                options: providerOptions,
            },
            {
                key: 'camera_id',
                label: 'Camera',
                controlType: 'select',
                options: cameras.map((camera) => ({
                    value: String(camera.id),
                    label: camera.label,
                })),
            },
        ];

        if (this.organizationService.canFilterByOrganization()) {
            filters.push({
                key: 'org_id',
                label: 'Organization',
                controlType: 'select',
                options: organizations.map((organization) => ({
                    value: String(organization.id),
                    label: organization.name,
                })),
            });
        }

        return filters;
    }

    loadAllFilterSupportData(orgId?: string): Observable<{
        cameras: CameraFilterOption[];
        organizations: OrganizationOption[];
    }> {
        return new Observable((subscriber) => {
            let cameras: CameraFilterOption[] = [];
            let organizations: OrganizationOption[] = [];
            let camerasDone = false;
            let organizationsDone = false;

            const emitIfReady = () => {
                if (camerasDone && organizationsDone) {
                    subscriber.next({cameras, organizations});
                    subscriber.complete();
                }
            };

            this.loadCameraOptions(orgId).subscribe({
                next: (result) => {
                    cameras = result;
                    camerasDone = true;
                    emitIfReady();
                },
                error: () => {
                    cameras = [];
                    camerasDone = true;
                    emitIfReady();
                },
            });

            if (this.organizationService.canFilterByOrganization()) {
                this.organizationService.loadOrganizationOptions().subscribe({
                    next: (result) => {
                        organizations = result;
                        organizationsDone = true;
                        emitIfReady();
                    },
                    error: () => {
                        organizations = [];
                        organizationsDone = true;
                        emitIfReady();
                    },
                });
            } else {
                organizationsDone = true;
                emitIfReady();
            }
        });
    }

    getFilterString(values: FilterValues, key: string): string | undefined {
        const value = values[key];
        if (typeof value === 'string' && value.trim()) {
            return value.trim();
        }
        return undefined;
    }
}
